/**
 * Client display-time utility — the SINGLE place the client's display
 * timezone is decided.
 *
 * The whole client renders time in **UTC** to match the MT5/Exness data
 * (which is UTC). Previously every chart formatter used local `get*()`
 * components, so a UTC trading day was shifted by the browser offset (most
 * visible on daily candles). See issue #90, "Client display zone must be UTC".
 *
 * Everything that DISPLAYS time (chart axis, crosshair, vertical-line labels)
 * and the analysis-request timezone route through here. The underlying
 * timestamps stay UTC unix-seconds (lightweight-charts requires that) — this
 * only controls the human-facing strings and how the analysis range is
 * interpreted.
 *
 * To make the zone configurable later (per-user offset = the deferred
 * "Option B"), change `zonedParts()` to use
 * `Intl.DateTimeFormat(undefined, { timeZone })` and add a setter; every
 * formatter below is built on `zonedParts`, so nothing else changes.
 */

export const DISPLAY_TZ = "UTC";

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
// Index 0 = Sunday (matches Date#getUTCDay).
const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const z2 = (n: number) => String(n).padStart(2, "0");

export interface TimeParts {
  year: number;
  month: number; // 0-11
  day: number; // 1-31
  hour: number;
  minute: number;
  second: number;
  weekday: number; // 0 = Sunday
}

/** Zoned calendar parts of a unix-SECONDS timestamp, in DISPLAY_TZ. This is
 *  the ONE place zoned components are read — swap the body to Intl to support
 *  arbitrary zones later. */
export function zonedParts(unixSeconds: number): TimeParts {
  const d = new Date(unixSeconds * 1000);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth(),
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    second: d.getUTCSeconds(),
    weekday: d.getUTCDay(),
  };
}

// --- chart axis tick-mark formatters ---

export function fmtAxisYear(ts: number): string {
  return String(zonedParts(ts).year);
}
export function fmtAxisMonth(ts: number): string {
  const p = zonedParts(ts);
  return `${SHORT_MONTHS[p.month]} '${z2(p.year % 100)}`;
}
export function fmtAxisDay(ts: number): string {
  const p = zonedParts(ts);
  return `${z2(p.day)} ${SHORT_MONTHS[p.month]}`;
}
export function fmtAxisTime(ts: number, withSeconds = false): string {
  const p = zonedParts(ts);
  return withSeconds
    ? `${z2(p.hour)}:${z2(p.minute)}:${z2(p.second)}`
    : `${z2(p.hour)}:${z2(p.minute)}`;
}

// --- crosshair + drawing labels ---

/** Crosshair tooltip: "Wed 10 Jun '26  14:30". `dateOnly` (daily+ timeframes)
 *  drops the time-of-day, which on a daily UTC bar would otherwise read a
 *  meaningless 00:00. */
export function fmtCrosshair(
  ts: number,
  opts: { dateOnly?: boolean } = {},
): string {
  const p = zonedParts(ts);
  const date = `${SHORT_DAYS[p.weekday]} ${z2(p.day)} ${SHORT_MONTHS[p.month]} '${z2(
    p.year % 100,
  )}`;
  return opts.dateOnly ? date : `${date}  ${z2(p.hour)}:${z2(p.minute)}`;
}

/** Vertical-line label: "2026-06-10 14:30" in DISPLAY_TZ. */
export function fmtLineLabel(ts: number): string {
  const p = zonedParts(ts);
  return `${p.year}-${z2(p.month + 1)}-${z2(p.day)} ${z2(p.hour)}:${z2(p.minute)}`;
}

// --- list / panel timestamps (trade journal, positions, notes) ---

/** Compact "MMM D, HH:MM" in DISPLAY_TZ — trade times, note timestamps, any
 *  panel that lists an instant. Matches the chart zone so a deal's time reads
 *  the same as the candle it sits on. */
export function fmtShortDateTime(unixSeconds: number): string {
  if (!unixSeconds) return "—";
  const p = zonedParts(unixSeconds);
  return `${SHORT_MONTHS[p.month]} ${p.day}, ${z2(p.hour)}:${z2(p.minute)}`;
}

/** Same as `fmtShortDateTime` but from an ISO string (e.g. a note's
 *  server-side `updatedAt`). Returns the raw string if unparseable. */
export function fmtShortDateTimeIso(iso: string): string {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? iso : fmtShortDateTime(Math.floor(ms / 1000));
}

/** Wall-clock "HH:MM:SS" in DISPLAY_TZ — freshness stamps (last-polled). */
export function fmtClock(unixSeconds: number): string {
  return fmtAxisTime(unixSeconds, true);
}

// --- analysis range ---

/** Timezone string sent with run-analysis requests. The backend interprets
 *  the start/end wall-clock strings in this zone (now UTC, matching the
 *  chart display + the underlying data). */
export function analysisTimezone(): string {
  return DISPLAY_TZ;
}

// --- DateTimePicker <-> timestamp (both interpreted in DISPLAY_TZ) ---

export interface WallClock {
  year: number;
  month: number; // 1-12 (picker-facing)
  day: number;
  hour: number;
  minute: number;
}

/** unix-seconds -> picker wall-clock parts (DISPLAY_TZ). Used to seed the
 *  analysis range from a chart timestamp so it matches the UTC chart. */
export function tsToWallClock(unixSeconds: number): WallClock {
  const p = zonedParts(unixSeconds);
  return {
    year: p.year,
    month: p.month + 1,
    day: p.day,
    hour: p.hour,
    minute: p.minute,
  };
}

/** picker wall-clock parts (DISPLAY_TZ) -> unix seconds. */
export function wallClockToTs(w: WallClock): number {
  return Math.floor(
    Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, 0) / 1000,
  );
}

/** unix-seconds -> "YYYY-MM-DD HH:MM:SS" in DISPLAY_TZ (the analysis
 *  start/end string format the backend expects alongside the tz). */
export function fmtWallClockString(unixSeconds: number): string {
  const p = zonedParts(unixSeconds);
  return `${p.year}-${z2(p.month + 1)}-${z2(p.day)} ${z2(p.hour)}:${z2(
    p.minute,
  )}:${z2(p.second)}`;
}

// --- datetime-local input strings ("YYYY-MM-DDTHH:mm") ---
// The shape the <input type="datetime-local"> / DateTimePicker use. Every
// "now"/seed string is built here so the zone choice lives in ONE place.

function nowUnixSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

/** unix-seconds -> "YYYY-MM-DDTHH:mm" in DISPLAY_TZ. */
export function tsToDateTimeLocal(unixSeconds: number): string {
  const p = zonedParts(unixSeconds);
  return `${p.year}-${z2(p.month + 1)}-${z2(p.day)}T${z2(p.hour)}:${z2(p.minute)}`;
}

/** "Now" as a DISPLAY_TZ datetime-local string (analysis "now" seed). */
export function nowDateTimeLocal(): string {
  return tsToDateTimeLocal(nowUnixSeconds());
}

/** Default analysis range: today 00:00 -> 23:59 in DISPLAY_TZ. The seed for a
 *  fresh tab's Start/End. */
export function defaultAnalysisRange(): { start: string; end: string } {
  const p = zonedParts(nowUnixSeconds());
  const day = `${p.year}-${z2(p.month + 1)}-${z2(p.day)}`;
  return { start: `${day}T00:00`, end: `${day}T23:59` };
}

/** "Today" calendar parts (month 0-11) in the chosen zone — drives a date
 *  picker's today-dot + default-open month. The ONE place the
 *  local-vs-UTC component branch lives; "utc" for UTC-interpreted pickers,
 *  "local" for broker-local ones (order expiry). */
export function nowDateParts(
  zone: "local" | "utc" = "utc",
): { y: number; m: number; d: number } {
  const n = new Date();
  return zone === "utc"
    ? { y: n.getUTCFullYear(), m: n.getUTCMonth(), d: n.getUTCDate() }
    : { y: n.getFullYear(), m: n.getMonth(), d: n.getDate() };
}

/** UTC "now" as a filename-safe stamp "YYYY-MM-DDTHH-MM-SS" (screenshots). */
export function nowFileStamp(): string {
  return new Date().toISOString().replace(/[:.]/g, "-").replace(/Z$/, "");
}

// --- relative + intentionally-local (the time exceptions, gathered here) ---
// Not UTC-zoned by nature; kept in this file so EVERY time helper has one home
// and the zone reasoning is visible in one place. See features/time_handling.md.

/** Compact countdown from seconds-remaining (zone-agnostic, pure math):
 *  "2d 3h" / "1h 5m" / "45m 12s" / "9s" / "exp". */
export function fmtCountdown(s: number): string {
  if (s <= 0) return "exp";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

/** "now / 5m ago / 3h ago / 2d ago" from an ISO timestamp. Relative-to-now, so
 *  it reads the browser wall clock by design (the delta is zone-invariant). */
export function fmtAgo(iso: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 45) return "now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** "12 Jan 2026, 14:03" from an ISO timestamp — browser-LOCAL, same as
 *  `fmtLocalDate` but with the clock time: for the admin dashboard's hover
 *  detail, where "3d ago" alone is too coarse to act on. */
export function fmtLocalDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "12 Jan 2026" from an ISO timestamp — intentionally browser-LOCAL: used
 *  only for account metadata (created/last-signin in the admin dashboard),
 *  date-only, not market data. */
export function fmtLocalDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
